import React, { useState, useEffect } from 'react';
import { ArrowLeft, Mail, User, Key, Lock, CheckCircle, AlertCircle, Shield, Eye, EyeOff } from 'lucide-react';
import logo from '../assets/logo.svg';

function RecoveryPage({ onBack }) {
  const [mode, setMode] = useState(null); // null, 'userid', 'password'

  if (!mode) {
    return (
      <div className="login-page">
        <div className="login-card" style={{ maxWidth: 440 }}>
          <div className="login-header">
            <img src={logo} alt="ZignaGenix" className="login-logo" />
            <h1 className="login-title">Account Recovery</h1>
            <p className="login-subtitle">Choose what you need to recover</p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '0 24px 24px' }}>
            <button
              className="login-button"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              onClick={() => setMode('userid')}
            >
              <User size={18} /> Recover My Username
            </button>
            <button
              className="login-button"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#2E8B98' }}
              onClick={() => setMode('password')}
            >
              <Key size={18} /> Reset My Password
            </button>
            <button
              onClick={onBack}
              style={{
                background: 'none', border: 'none', color: '#6C757D', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '10px', fontSize: 14
              }}
            >
              <ArrowLeft size={16} /> Back to Sign In
            </button>
          </div>

          <div className="login-footer">
            <p className="login-hipaa-notice">
              <Shield size={12} /> All recovery attempts are logged per HIPAA audit requirements.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'userid') {
    return <UserIdRecovery onBack={() => setMode(null)} onBackToLogin={onBack} />;
  }

  return <PasswordReset onBack={() => setMode(null)} onBackToLogin={onBack} />;
}

// ─── UserID Recovery Flow ──────────────────────────────────────────────────

function UserIdRecovery({ onBack, onBackToLogin }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recoveryData, setRecoveryData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [recoveredUsername, setRecoveredUsername] = useState(null);

  async function handleEmailSubmit(e) {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Please enter your email address'); return; }

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.recoverUserIdStep1(email);
      if (result.error) {
        setError(result.message);
      } else {
        setRecoveryData(result);
        setStep(2);
      }
    } catch (err) {
      setError('Recovery failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleAnswersSubmit(e) {
    e.preventDefault();
    setError('');

    const answerArray = recoveryData.questions.map((q) => ({
      index: q.index,
      answer: answers[q.index] || '',
    }));

    if (answerArray.some((a) => !a.answer.trim())) {
      setError('Please answer all security questions');
      return;
    }

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.recoverUserIdStep2(recoveryData.userId, answerArray);
      if (result.error) {
        setError(result.message);
      } else {
        setRecoveredUsername(result);
        setStep(3);
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: 480 }}>
        <div className="login-header">
          <img src={logo} alt="ZignaGenix" className="login-logo" />
          <h1 className="login-title">Recover Username</h1>
          <p className="login-subtitle">
            {step === 1 && 'Enter the email associated with your account'}
            {step === 2 && 'Answer your security questions'}
            {step === 3 && 'Username recovered successfully'}
          </p>
        </div>

        {step === 1 && (
          <form className="login-form" onSubmit={handleEmailSubmit}>
            {error && <div className="login-error"><AlertCircle size={16} /><span>{error}</span></div>}
            <div className="login-field">
              <label className="login-label" htmlFor="recovery-email"><Mail size={14} /> Email Address</label>
              <input
                id="recovery-email" type="email" className="login-input"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@zignagenix.com" autoFocus disabled={loading}
              />
            </div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Verifying...' : 'Continue'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form className="login-form" onSubmit={handleAnswersSubmit}>
            {error && <div className="login-error"><AlertCircle size={16} /><span>{error}</span></div>}
            {recoveryData.questions.map((q) => (
              <div className="login-field" key={q.index}>
                <label className="login-label"><Lock size={14} /> {q.text}</label>
                <input
                  type="text" className="login-input"
                  value={answers[q.index] || ''}
                  onChange={(e) => setAnswers({ ...answers, [q.index]: e.target.value })}
                  placeholder="Your answer" disabled={loading}
                />
              </div>
            ))}
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify Answers'}
            </button>
          </form>
        )}

        {step === 3 && recoveredUsername && (
          <div style={{ padding: '0 24px 24px', textAlign: 'center' }}>
            <div style={{ background: '#e8f5e9', borderRadius: 12, padding: 20, marginBottom: 16 }}>
              <CheckCircle size={32} color="#43a047" style={{ marginBottom: 8 }} />
              <p style={{ fontSize: 14, color: '#2e7d32', marginBottom: 8 }}>Your username is:</p>
              <p style={{ fontSize: 22, fontWeight: 700, color: '#212529', fontFamily: 'monospace' }}>
                {recoveredUsername.username}
              </p>
              <p style={{ fontSize: 13, color: '#6C757D', marginTop: 8 }}>
                ({recoveredUsername.displayName})
              </p>
            </div>
            <button className="login-button" onClick={onBackToLogin}>
              Back to Sign In
            </button>
          </div>
        )}

        {step !== 3 && (
          <div style={{ textAlign: 'center', padding: '0 24px 16px' }}>
            <button onClick={onBack} style={{
              background: 'none', border: 'none', color: '#6C757D', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14
            }}>
              <ArrowLeft size={16} /> Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Password Reset Flow ───────────────────────────────────────────────────

function PasswordReset({ onBack, onBackToLogin }) {
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recoveryData, setRecoveryData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);

  // Auto-redirect to login after successful password reset
  useEffect(() => {
    if (step === 3 && success) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            onBackToLogin();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [step, success, onBackToLogin]);

  async function handleUsernameSubmit(e) {
    e.preventDefault();
    setError('');
    if (!username.trim()) { setError('Please enter your username'); return; }

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.resetPasswordStep1(username);
      if (result.error) {
        setError(result.message);
      } else {
        setRecoveryData(result);
        setStep(2);
      }
    } catch (err) {
      setError('Recovery failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleResetSubmit(e) {
    e.preventDefault();
    setError('');

    const answerArray = recoveryData.questions.map((q) => ({
      index: q.index,
      answer: answers[q.index] || '',
    }));

    if (answerArray.some((a) => !a.answer.trim())) {
      setError('Please answer all security questions');
      return;
    }

    if (!newPassword) { setError('Please enter a new password'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 12) { setError('Password must be at least 12 characters'); return; }

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.resetPasswordStep2(
        recoveryData.userId, answerArray, newPassword
      );
      if (result.error) {
        setError(result.message);
      } else {
        setSuccess(true);
        setStep(3);
      }
    } catch (err) {
      setError('Password reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: 480 }}>
        <div className="login-header">
          <img src={logo} alt="ZignaGenix" className="login-logo" />
          <h1 className="login-title">Reset Password</h1>
          <p className="login-subtitle">
            {step === 1 && 'Enter your username to begin'}
            {step === 2 && 'Answer security questions and set a new password'}
            {step === 3 && 'Password reset complete'}
          </p>
        </div>

        {step === 1 && (
          <form className="login-form" onSubmit={handleUsernameSubmit}>
            {error && <div className="login-error"><AlertCircle size={16} /><span>{error}</span></div>}
            <div className="login-field">
              <label className="login-label" htmlFor="reset-username"><User size={14} /> Username</label>
              <input
                id="reset-username" type="text" className="login-input"
                value={username} onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username" autoFocus disabled={loading}
              />
            </div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Verifying...' : 'Continue'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form className="login-form" onSubmit={handleResetSubmit} autoComplete="off" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {error && <div className="login-error"><AlertCircle size={16} /><span>{error}</span></div>}

            <p style={{ fontSize: 13, color: '#6C757D', padding: '0 0 8px', fontWeight: 600 }}>Security Questions</p>
            {recoveryData.questions.map((q) => (
              <div className="login-field" key={q.index}>
                <label className="login-label"><Lock size={14} /> {q.text}</label>
                <input
                  type="text" className="login-input"
                  value={answers[q.index] || ''}
                  onChange={(e) => setAnswers({ ...answers, [q.index]: e.target.value })}
                  placeholder="Your answer" disabled={loading}
                />
              </div>
            ))}

            <hr style={{ border: 'none', borderTop: 'none', margin: '8px 0' }} />
            <p style={{ fontSize: 13, color: '#6C757D', padding: '0 0 8px', fontWeight: 600 }}>New Password</p>

            <div className="login-field">
              <label className="login-label"><Key size={14} /> New Password</label>
              <div className="login-password-wrapper">
                <input
                  type={showNewPassword ? 'text' : 'password'} className="login-input"
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 12 chars, upper+lower+number+special" disabled={loading}
                  autoComplete="new-password"
                />
                <button type="button" className="login-password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  tabIndex={-1}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}>
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="login-field">
              <label className="login-label"><Key size={14} /> Confirm Password</label>
              <div className="login-password-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'} className="login-input"
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password" disabled={loading}
                  autoComplete="new-password"
                />
                <button type="button" className="login-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <PasswordStrengthIndicator password={newPassword} />

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}

        {step === 3 && success && (
          <div style={{ padding: '0 24px 24px', textAlign: 'center' }}>
            <div style={{ background: '#e8f5e9', borderRadius: 12, padding: 20, marginBottom: 16 }}>
              <CheckCircle size={32} color="#43a047" style={{ marginBottom: 8 }} />
              <p style={{ fontSize: 16, color: '#2e7d32', fontWeight: 600 }}>Password Reset Successful</p>
              <p style={{ fontSize: 13, color: '#6C757D', marginTop: 8 }}>
                You can now sign in with your new password.
              </p>
              <p style={{ fontSize: 12, color: '#9E9E9E', marginTop: 12 }}>
                Redirecting to sign in{countdown > 0 ? ` in ${countdown}s` : ''}...
              </p>
            </div>
            <button className="login-button" onClick={onBackToLogin}>
              Back to Sign In Now
            </button>
          </div>
        )}

        {step !== 3 && (
          <div style={{ textAlign: 'center', padding: '0 24px 16px' }}>
            <button onClick={onBack} style={{
              background: 'none', border: 'none', color: '#6C757D', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14
            }}>
              <ArrowLeft size={16} /> Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Password Strength Indicator ───────────────────────────────────────────

function PasswordStrengthIndicator({ password }) {
  if (!password) return null;

  const checks = [
    { label: '12+ characters', met: password.length >= 12 },
    { label: 'Uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'Lowercase letter', met: /[a-z]/.test(password) },
    { label: 'Number', met: /[0-9]/.test(password) },
    { label: 'Special character', met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password) },
  ];

  const metCount = checks.filter((c) => c.met).length;
  const barColor = metCount <= 2 ? '#e53935' : metCount <= 3 ? '#f9a825' : metCount <= 4 ? '#2E8B98' : '#43a047';

  return (
    <div style={{ padding: '0 0 8px' }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: i <= metCount ? barColor : '#e2e8f0',
            transition: 'background 0.2s'
          }} />
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
        {checks.map((c) => (
          <span key={c.label} style={{ fontSize: 11, color: c.met ? '#43a047' : '#ADB5BD' }}>
            {c.met ? '\u2713' : '\u2717'} {c.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export default RecoveryPage;
export { PasswordStrengthIndicator };
